import React, { useState } from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import { roundTo0Decimal } from 'src/utils/numberHelper';
import { getAllTanks } from 'src/utils/stowage/tankPlanUtils';
import {
    BottomHelpPanel,
    BottomHelpPanelContainer,
    BottomHelpPanelMenuItem,
    LeftBottomHelpPanel,
    RightBottomHelpPanel,
} from './TankPlanBottomPanel.styles';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BottomTankPlanMenuWrapperComponent from './BottomTankPlanContextMenuWrapper';
import { useRotationStore } from 'src/context/RotationContext';
import { getTankContentForCondition } from 'src/utils/stowage/manifest/tankPlanConditionUtils';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { useAuthStore } from 'src/context/AuthContext';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';

const TankPlanBottomPanel: React.FC = () => {
    const {
        tankPlan,
        activeStowagePlan,
        tankPlanBottomContextMenuState,
        setTankPlanBottomContextMenuState,
        setTankPlanReset,
        tankPlanMove,
        setTankPlanMove,
        tankPlanZoomInScale,
        setTankPlanZoomInScale,
        selectedTankPlanCondition,
        useDynamicTankScaling,
        setUseDynamicTankScaling,
    } = useStowageStore();
    const { selectedTanks, setSelectedTanks } = useStowageSelectionStore();
    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall } = useRotationStore();
    const [isBottomPanelExpanded] = useState<boolean>(true);
    const { role } = useAuthStore();

    const getTotalVolume = () => {
        const _selectedTanks = selectedTanks;
        let _totalVolume = 0;
        _selectedTanks?.forEach((t) => {
            _totalVolume += t?.volumeAt98Percent;
        });

        return roundTo0Decimal(_totalVolume);
    };

    const onClearSelectedTanks = () => {
        setSelectedTanks([]);
    };

    const onSelectAll = () => {
        if (tankPlan) {
            const _remainingTanks = [];
            const _allTanks = getAllTanks(tankPlan);
            _allTanks.forEach((tank) => {
                const tankConditionTankContent = getTankContentForCondition(
                    activeStowagePlan,
                    selectedStowageRotationPortCall,
                    selectedStowageRotationBerthCall,
                    selectedTankPlanCondition,
                    tank
                );

                if (tankConditionTankContent.cargoContents.length === 0) {
                    _remainingTanks.push(tank);
                }
            });

            setSelectedTanks([..._remainingTanks]);
        }
    };

    const onZoomOut = () => {
        if (tankPlan) {
            const _tankPlanZoomOutScale = tankPlanZoomInScale - 0.1;
            setTankPlanZoomInScale(_tankPlanZoomOutScale);
        }
    };

    const onZoomIn = () => {
        if (tankPlan) {
            const _tankPlanZoomInScale = tankPlanZoomInScale + 0.1;
            setTankPlanZoomInScale(_tankPlanZoomInScale);
        }
    };

    const onZoomReset = () => {
        if (tankPlan) {
            setTankPlanReset(true);
        }
    };

    const onMove = () => {
        if (tankPlan) {
            setTankPlanMove(!tankPlanMove);
        }
    };

    const onToggleDynamicScaling = () => {
        setUseDynamicTankScaling(!useDynamicTankScaling);
    };

    const onOpenBottomHelpMenu = (e) => {
        e.stopPropagation();

        setTankPlanBottomContextMenuState({
            visible: !tankPlanBottomContextMenuState.visible,
            tank: null,
            cargoContents: [],
            cargoOperationGuid: null,
            berthCall: null,
            x: 0,
            y: -50,
            violations: null,
        });
    };

    return (
        <>
            {isBottomPanelExpanded && (
                <>
                    <BottomHelpPanelContainer>
                        <BottomHelpPanel>
                            <LeftBottomHelpPanel>
                                <div>
                                    <h4>Tank</h4>
                                    <span className="">Volume {activeStowagePlan && <span>{getTotalVolume()} m³ </span>}</span>
                                </div>
                            </LeftBottomHelpPanel>
                            <RightBottomHelpPanel>
                                <BottomHelpPanelMenuItem className="first" onClick={() => onSelectAll()} title={'Select all available tanks'}>
                                    <FontAwesomeIcon icon="hand-pointer" />
                                </BottomHelpPanelMenuItem>
                                <BottomHelpPanelMenuItem onClick={() => onClearSelectedTanks()} title={'Clear tank selection'}>
                                    <FontAwesomeIcon icon="window-close" />
                                </BottomHelpPanelMenuItem>
                                <BottomHelpPanelMenuItem onClick={() => onZoomIn()} title={'Zoom in'}>
                                    <FontAwesomeIcon icon="plus" />
                                </BottomHelpPanelMenuItem>
                                <BottomHelpPanelMenuItem onClick={() => onZoomOut()} title={'Zoom out'}>
                                    <FontAwesomeIcon icon="minus" />
                                </BottomHelpPanelMenuItem>
                                <BottomHelpPanelMenuItem onClick={() => onZoomReset()} title={'Reset zoom'}>
                                    <FontAwesomeIcon icon="rotate-left" />
                                </BottomHelpPanelMenuItem>
                                <BottomHelpPanelMenuItem onClick={() => onMove()} className={tankPlanMove ? 'selected' : ''} title={'Move tank plan'}>
                                    <FontAwesomeIcon icon="up-down-left-right" />
                                </BottomHelpPanelMenuItem>
                                <BottomHelpPanelMenuItem
                                    onClick={onToggleDynamicScaling}
                                    className={useDynamicTankScaling ? 'selected' : ''}
                                    title={'Toggle dynamic tank scaling'}
                                >
                                    <FontAwesomeIcon icon="table" />
                                </BottomHelpPanelMenuItem>
                                {hasPermission(role, stowageFeatureEnum.EditStowage) && (
                                    <BottomHelpPanelMenuItem onClick={(e) => onOpenBottomHelpMenu(e)} title="Open menu">
                                        <FontAwesomeIcon icon="ellipsis-h" />
                                    </BottomHelpPanelMenuItem>
                                )}
                                {tankPlanBottomContextMenuState.visible && <BottomTankPlanMenuWrapperComponent />}
                            </RightBottomHelpPanel>
                        </BottomHelpPanel>
                    </BottomHelpPanelContainer>
                </>
            )}
        </>
    );
};

export default TankPlanBottomPanel;
